import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../utils/api";

export type PhotorealisticScanResult = {
  fileId: number;
  file_name: string;
  file_type: string;
  folder_name: string;
  case_id: string;
  source_file_id: number;
  source_file_name: string;
};

export type StandaloneRecolourResult = {
  image: string;
  file_name: string;
  source_file_name: string;
  remainingCredits: number | null;
  usageCount?: number | null;
};

export type PhotoRecolourUsage = {
  usageCount: number | null;
  scope: "employee" | "account" | "credits";
  emp_id: number | null;
  emp_name: string | null;
  emp_role: string | null;
};

export type StandaloneSession = {
  originalUrl: string | null;
  originalName: string | null;
  generatedUrl: string | null;
  generatedName: string | null;
};

type PhotorealisticScanResponse = {
  success: boolean;
  message: string;
  data?: PhotorealisticScanResult & { usageCount?: number | null };
};

type StandaloneRecolourResponse = {
  success: boolean;
  message: string;
  data?: StandaloneRecolourResult;
};

type PhotorealisticScanState = {
  loading: boolean;
  generatingFileId: number | null;
  error: string | null;
  lastResult: PhotorealisticScanResult | null;
  standaloneLoading: boolean;
  standaloneError: string | null;
  standaloneResult: StandaloneRecolourResult | null;
  standaloneSession: StandaloneSession;
  usage: PhotoRecolourUsage | null;
  usageLoading: boolean;
};

type GeneratePayload = {
  case_id: string;
  source_file_id: number;
  user_id: number;
};

type StandaloneGeneratePayload = {
  file: File;
  user_id: number;
};

let standaloneOriginalFile: File | null = null;
let standaloneGeneratedBlob: Blob | null = null;

export const getStandaloneOriginalFile = () => standaloneOriginalFile;
export const getStandaloneGeneratedBlob = () => standaloneGeneratedBlob;

const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/data:(.*?);/)?.[1] || "image/png";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
};

const initialState: PhotorealisticScanState = {
  loading: false,
  generatingFileId: null,
  error: null,
  lastResult: null,
  standaloneLoading: false,
  standaloneError: null,
  standaloneResult: null,
  standaloneSession: {
    originalUrl: null,
    originalName: null,
    generatedUrl: null,
    generatedName: null,
  },
  usage: null,
  usageLoading: false,
};

export const fetchPhotoRecolourUsage = createAsyncThunk<
  PhotoRecolourUsage,
  void,
  { rejectValue: string }
>("photorealisticScan/fetchUsage", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get<{
      success: boolean;
      message: string;
      data?: PhotoRecolourUsage;
    }>("/api/photo-recolour/usage");

    if (!response.data?.success || !response.data.data) {
      return rejectWithValue(
        response.data?.message || "Failed to load Photo Recolour usage"
      );
    }

    return response.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to load Photo Recolour usage"
    );
  }
});

export const generatePhotorealisticScan = createAsyncThunk<
  PhotorealisticScanResult & { usageCount?: number | null },
  GeneratePayload,
  { rejectValue: string }
>(
  "photorealisticScan/generate",
  async ({ case_id, source_file_id, user_id }, { rejectWithValue }) => {
    try {
      const response = await api.post<PhotorealisticScanResponse>(
        "/api/box/photorealistic-scan",
        { case_id, source_file_id, user_id },
        { timeout: 300_000 }
      );

      if (!response.data?.success || !response.data.data) {
        return rejectWithValue(
          response.data?.message || "Realistic image generation failed"
        );
      }

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Realistic image generation failed"
      );
    }
  }
);

export const generateStandaloneRecolour = createAsyncThunk<
  StandaloneRecolourResult,
  StandaloneGeneratePayload,
  { rejectValue: string }
>(
  "photorealisticScan/generateStandalone",
  async ({ file, user_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("user_id", String(user_id));

      const response = await api.post<StandaloneRecolourResponse>(
        "/api/photo-recolour/generate",
        formData,
        {
          timeout: 300_000,
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        }
      );

      if (!response.data?.success || !response.data.data) {
        return rejectWithValue(
          response.data?.message || "Realistic image generation failed"
        );
      }

      const data = response.data.data;
      standaloneGeneratedBlob = dataUrlToBlob(data.image);

      return {
        ...data,
        image: URL.createObjectURL(standaloneGeneratedBlob),
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Realistic image generation failed"
      );
    }
  }
);

const photorealisticScanSlice = createSlice({
  name: "photorealisticScan",
  initialState,
  reducers: {
    clearPhotorealisticScanError: (state) => {
      state.error = null;
    },
    setStandaloneOriginal: (
      state,
      action: PayloadAction<{ url: string; name: string }>
    ) => {
      if (state.standaloneSession.generatedUrl) {
        URL.revokeObjectURL(state.standaloneSession.generatedUrl);
      }
      if (
        state.standaloneSession.originalUrl &&
        state.standaloneSession.originalUrl !== action.payload.url
      ) {
        URL.revokeObjectURL(state.standaloneSession.originalUrl);
      }
      standaloneGeneratedBlob = null;
      state.standaloneSession.originalUrl = action.payload.url;
      state.standaloneSession.originalName = action.payload.name;
      state.standaloneSession.generatedUrl = null;
      state.standaloneSession.generatedName = null;
      state.standaloneResult = null;
      state.standaloneError = null;
    },
    clearStandaloneRecolour: (state) => {
      state.standaloneError = null;
      state.standaloneResult = null;
      state.standaloneLoading = false;
    },
    resetPhotorealisticScanState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhotoRecolourUsage.pending, (state) => {
        state.usageLoading = true;
      })
      .addCase(fetchPhotoRecolourUsage.fulfilled, (state, action) => {
        state.usageLoading = false;
        state.usage = action.payload;
      })
      .addCase(fetchPhotoRecolourUsage.rejected, (state) => {
        state.usageLoading = false;
      })
      .addCase(generatePhotorealisticScan.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.generatingFileId = action.meta.arg.source_file_id;
      })
      .addCase(generatePhotorealisticScan.fulfilled, (state, action) => {
        state.loading = false;
        state.generatingFileId = null;
        state.lastResult = action.payload;
        if (
          action.payload.usageCount != null &&
          state.usage &&
          state.usage.scope !== "credits"
        ) {
          state.usage = {
            ...state.usage,
            usageCount: action.payload.usageCount,
          };
        }
      })
      .addCase(generatePhotorealisticScan.rejected, (state, action) => {
        state.loading = false;
        state.generatingFileId = null;
        state.error = action.payload || "Realistic image generation failed";
      })
      .addCase(generateStandaloneRecolour.pending, (state) => {
        state.standaloneLoading = true;
        state.standaloneError = null;
      })
      .addCase(generateStandaloneRecolour.fulfilled, (state, action) => {
        state.standaloneLoading = false;
        state.standaloneResult = action.payload;
        if (state.standaloneSession.generatedUrl) {
          URL.revokeObjectURL(state.standaloneSession.generatedUrl);
        }
        state.standaloneSession.generatedUrl = action.payload.image;
        state.standaloneSession.generatedName = action.payload.file_name;
        if (
          action.payload.usageCount != null &&
          state.usage &&
          state.usage.scope !== "credits"
        ) {
          state.usage = {
            ...state.usage,
            usageCount: action.payload.usageCount,
          };
        }
      })
      .addCase(generateStandaloneRecolour.rejected, (state, action) => {
        state.standaloneLoading = false;
        state.standaloneError =
          action.payload || "Realistic image generation failed";
      });
  },
});

export const stageStandaloneOriginal = (file: File) => {
  standaloneOriginalFile = file;
  return photorealisticScanSlice.actions.setStandaloneOriginal({
    url: URL.createObjectURL(file),
    name: file.name,
  });
};

export const {
  clearPhotorealisticScanError,
  setStandaloneOriginal,
  clearStandaloneRecolour,
  resetPhotorealisticScanState,
} = photorealisticScanSlice.actions;

export default photorealisticScanSlice.reducer;
